import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Language selector matching the public website's nav dropdown: flag, full
 * language name, caret, and a navy panel whose active entry is copper.
 *
 * The flags are inline SVG rather than emoji on purpose. Windows renders
 * regional-indicator emoji as bare letter pairs ("GB", "FR") instead of flags,
 * so an emoji version would not look like the site on most of the audience's
 * machines.
 */

const UkFlag: React.FC = () => (
  <svg viewBox="0 0 60 30" aria-hidden="true" className="w-[18px] h-[12px] rounded-[2px] flex-shrink-0">
    <rect width="60" height="30" fill="#012169" />
    <path d="M0 0l60 30M60 0L0 30" stroke="#fff" strokeWidth="6" />
    <path d="M0 0l60 30M60 0L0 30" stroke="#C8102E" strokeWidth="3.5" />
    <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10" />
    <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);

const FrFlag: React.FC = () => (
  <svg viewBox="0 0 60 30" aria-hidden="true" className="w-[18px] h-[12px] rounded-[2px] flex-shrink-0">
    <rect width="20" height="30" fill="#002395" />
    <rect x="20" width="20" height="30" fill="#fff" />
    <rect x="40" width="20" height="30" fill="#ED2939" />
  </svg>
);

const LANGS = [
  { code: 'en', label: 'English', Flag: UkFlag },
  { code: 'fr', label: 'Français', Flag: FrFlag },
] as const;

export const LanguageDropdown: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const current = i18n.language?.startsWith('fr') ? LANGS[1] : LANGS[0];
  const CurrentFlag = current.Flag;

  // Close on outside click and on Escape, matching the site's behaviour.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Keep <html lang> in step with the UI language. It was stuck on "en"
  // whatever the user picked, which misleads screen readers and browser
  // translation. Runs on mount too, so a restored FR session is announced right.
  useEffect(() => {
    document.documentElement.lang = current.code;
  }, [current.code]);

  const choose = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('header.language')}
        className="inline-flex items-center gap-[0.45rem] rounded-[2px] border border-[#EDE6DE] px-[0.7rem] py-[0.4rem]
                   text-xs font-semibold tracking-[0.03em] text-[#1B2A4A] bg-transparent
                   hover:border-[#C5922E] transition-colors duration-300"
      >
        <CurrentFlag />
        <span>{current.label}</span>
        <svg
          viewBox="0 0 12 12"
          aria-hidden="true"
          className={`w-[10px] h-[10px] fill-none stroke-current transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          strokeWidth="2"
        >
          <path d="M2 4l4 4 4-4" />
        </svg>
      </button>

      {/* Navy panel with copper active state, as on the website.
          Note the underscores in top-[calc(100%_+_0.5rem)]: Tailwind turns them
          into the spaces that calc() requires, and without them the generated
          CSS is invalid and the panel mispositions. */}
      <div
        className={`absolute right-0 top-[calc(100%_+_0.5rem)] min-w-[160px] rounded-[3px] bg-[#243656]
                    border border-white/[0.12] shadow-[0_8px_24px_rgba(0,0,0,0.35)] z-[1001]
                    transition-[opacity,transform,visibility] duration-[250ms] ease-out ${
                      open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1.5'
                    }`}
        role="menu"
      >
        {LANGS.map(({ code, label, Flag }) => {
          const active = current.code === code;
          return (
            <button
              key={code}
              type="button"
              role="menuitem"
              onClick={() => choose(code)}
              className={`flex w-full items-center gap-[0.55rem] px-[0.85rem] py-[0.6rem] text-left text-[0.78rem] font-medium
                          transition-colors hover:bg-white/[0.06] ${
                            active ? 'text-[#C5922E]' : 'text-white/85 hover:text-[#C5922E]'
                          }`}
            >
              <Flag />
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
